"use client";
import { useEffect, useState, useRef } from "react";
import { throttle } from "lodash-es";

type ScrollDirection = "up" | "down" | null;

interface UseScrollDirectionOptions {
    threshold?: number;
    throttleDelay?: number;
    deadZone?: number;
}

export const useScrollDirection = (options: UseScrollDirectionOptions = {}) => {
    const { threshold = 5, throttleDelay = 80, deadZone = 0 } = options;

    const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(null);
    const [isInDeadzone, setIsInDeadzone] = useState<boolean>(true);
    const lastScrollY = useRef<number>(0);
    const rafId = useRef<number | null>(null);

    useEffect(() => {
        lastScrollY.current = window.scrollY;

        const updateScrollDirection = () => {
            const currentScrollY = window.scrollY;

            // We're at the top, always show the banner
            if (currentScrollY <= deadZone) {
                setScrollDirection(null);
                setIsInDeadzone(true);
                lastScrollY.current = currentScrollY;
                return;
            }

            // Calculate the direction only if we've scrolled more than the threshold
            const scrollDifference = currentScrollY - lastScrollY.current;

            if (Math.abs(scrollDifference) < threshold) {
                return;
            }

            if (scrollDifference > 0) {
                setScrollDirection("down");
                setIsInDeadzone(false);
            } else if (scrollDifference < 0) {
                setScrollDirection("up");
                setIsInDeadzone(false);
            }

            lastScrollY.current = currentScrollY;
        };

        const rafUpdateScrollDirection = () => {
            if (rafId.current !== null) {
                cancelAnimationFrame(rafId.current);
            }

            rafId.current = requestAnimationFrame(updateScrollDirection);
        };

        const throttledUpdateScrollDirection = throttle(rafUpdateScrollDirection, throttleDelay);

        window.addEventListener("scroll", throttledUpdateScrollDirection);

        return () => {
            window.removeEventListener("scroll", throttledUpdateScrollDirection);
            throttledUpdateScrollDirection.cancel();
            if (rafId.current !== null) {
                cancelAnimationFrame(rafId.current);
            }
        };
    }, [threshold, throttleDelay, deadZone]);

    return { scrollDirection, isInDeadzone };
};
